import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { ASSETS } from "@/data/content";
import { useI18n } from "@/i18n";
import { SectionCta } from "@/components/SectionCta";

const IMAGES = [
  { src: ASSETS.workationMatcha, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.atmBook, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.atmGarden, ratio: "aspect-[4/3]", position: "object-center" },
  { src: ASSETS.atmHedgehog, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.atmCouple, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.atmFire, ratio: "aspect-[3/4]", position: "object-[center_62%]" },
  { src: ASSETS.atmSunflowers, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.atmRunning, ratio: "aspect-[3/4]", position: "object-center" },
  { src: ASSETS.workationDesk, ratio: "aspect-[3/4]", position: "object-center" },
];

export default function Atmosphere() {
  const { t } = useI18n();
  const a = t.atmosphere;
  const MOMENTS = a.moments.map((m, i) => ({
    time: m.time,
    title: m.title,
    body: m.body,
    image: { src: IMAGES[i].src, alt: m.alt, ratio: IMAGES[i].ratio, position: IMAGES[i].position },
  }));
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  return (
    <section
      id="atmosfera"
      data-testid="atmosphere-section"
      className="relative bg-canvas-surface py-14 md:py-20 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro */}
        <div className="mb-16 md:mb-24 max-w-3xl">
          <Reveal>
            <p className="overline mb-6">{a.overline}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
              {a.headingA} <span className="italic text-brand-red/90">{a.headingB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-ink-muted text-base md:text-lg leading-relaxed">
              {a.intro}
            </p>
          </Reveal>
        </div>

        {/* Timeline of the day */}
        <div className="relative">
          {/* vertical timeline line */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-ink/10"
          />

          <div className="flex flex-col gap-20 md:gap-28">
            {MOMENTS.map((m, i) => {
              const reverse = i % 2 === 1;
              return (
                <Stagger
                  key={m.time}
                  className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center"
                >
                  {/* timeline node */}
                  <div
                    aria-hidden="true"
                    className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-brand-red ring-4 ring-canvas z-10"
                  />

                  {/* Text */}
                  <div
                    className={`${
                      reverse ? "lg:order-2 lg:pl-16" : "lg:pr-16 lg:text-right lg:items-end"
                    } flex flex-col`}
                  >
                    <StaggerItem>
                      <span className="serif text-brand-red/30 text-6xl md:text-7xl font-light leading-none block">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="overline text-brand-red mt-4 mb-4">
                        {m.time}
                      </p>
                      <h3 className="serif text-2xl md:text-3xl lg:text-4xl font-light leading-tight text-ink">
                        {m.title}
                      </h3>
                      <p
                        className={`mt-6 text-ink-muted text-base leading-relaxed max-w-md ${
                          reverse ? "" : "lg:ml-auto"
                        }`}
                      >
                        {m.body}
                      </p>
                    </StaggerItem>
                  </div>

                  {/* Image */}
                  <div className={reverse ? "lg:order-1" : ""}>
                    <StaggerItem>
                      {m.image.placeholder ? (
                        <div
                          data-testid={`atmosphere-image-${i}`}
                          className={`w-full ${m.image.ratio} border border-dashed border-ink/25 bg-canvas-soft flex items-center justify-center p-8`}
                        >
                          <span className="overline text-ink-muted text-center max-w-[16rem]">
                            {m.image.label}
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setLightbox(m.image)}
                          data-testid={`atmosphere-image-${i}`}
                          aria-label={`Powiększ zdjęcie: ${m.image.alt}`}
                          className={`group block w-full ${m.image.ratio} overflow-hidden cursor-zoom-in`}
                        >
                          <img
                            src={m.image.src}
                            alt={m.image.alt}
                            loading="lazy"
                            className={`w-full h-full object-cover ${m.image.position} group-hover:scale-[1.04] transition-transform duration-700 ease-out`}
                          />
                        </button>
                      )}
                    </StaggerItem>
                  </div>
                </Stagger>
              );
            })}
          </div>
        </div>

        {/* CTA — naturalne przejścia do innych zakładek */}
        <Reveal className="mt-16 md:mt-24 block">
          <div className="border-t border-ink/10 pt-12 md:pt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <p className="serif text-2xl md:text-3xl font-light text-ink max-w-lg leading-snug">
              {a.ctaTitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
              <SectionCta to="/okolica" testid="atmosphere-cta-okolica">
                {a.ctaOkolica}
              </SectionCta>
              <SectionCta to="/workation" testid="atmosphere-cta-workation">
                {a.ctaWorkation}
              </SectionCta>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="atmosphere-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
          >
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="atmosphere-lightbox-close"
              aria-label="Zamknij"
              className="absolute top-6 right-6 z-10 text-canvas/80 hover:text-canvas transition-colors"
            >
              <X size={28} strokeWidth={1.4} />
            </button>
            <motion.img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
